import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PermissionActionValue, PermissionRead, User } from '../types';
import { authApi } from '../services/resources';
import { registerUnauthorizedHandler, getApiErrorMessage } from '../services/api';
import { tokenStorage } from '../services/tokenStorage';

interface AuthState {
  user: User | null;
  roles: string[];
  permissions: PermissionRead[];
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Resolves with the freshly-fetched roles/permissions — useful right after
   * login, before this hook's own state has re-rendered into the caller. */
  login: (username: string, password: string) => Promise<{ roles: string[]; permissions: PermissionRead[] }>;
  logout: () => void;
  /** Checks the resolved `permissions` array from login; "Super Admin" always passes, same as before. */
  can: (module: string, action: PermissionActionValue) => boolean;
  /** Merges fields into the current user (e.g. clearing must_change_password after a successful change) without a full re-login. */
  updateUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const SESSION_KEY = 'ilms_session';

function readSession(): AuthState {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return { user: null, roles: [], permissions: [] };
    const parsed = JSON.parse(raw) as AuthState;
    return parsed;
  } catch {
    return { user: null, roles: [], permissions: [] };
  }
}

function writeSession(state: AuthState): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => readSession());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const logout = useCallback(() => {
    tokenStorage.clear();
    localStorage.removeItem(SESSION_KEY);
    setState({ user: null, roles: [], permissions: [] });
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setState({ user: null, roles: [], permissions: [] });
      localStorage.removeItem(SESSION_KEY);
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      // v2's login response returns the resolved roles AND permission grants in
      // the same call — no follow-up round-trip needed to build `can()`.
      const res = await authApi.login({ username, password });
      tokenStorage.setTokens(res.access_token, res.refresh_token);
      const next: AuthState = { user: res.user, roles: res.roles, permissions: res.permissions };
      setState(next);
      writeSession(next);
      return { roles: next.roles, permissions: next.permissions };
    } catch (err) {
      const message = getApiErrorMessage(err, 'Unable to sign in. Please check your username and password.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateUser = useCallback((patch: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const next = { ...prev, user: { ...prev.user, ...patch } };
      writeSession(next);
      return next;
    });
  }, []);

  const can = useCallback(
    (module: string, action: PermissionActionValue) => {
      if (state.roles.includes('Super Admin')) return true;
      return state.permissions.some((p) => p.module === module && p.action === action);
    },
    [state.permissions, state.roles]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      isAuthenticated: !!state.user && !!tokenStorage.getAccessToken(),
      isLoading,
      error,
      login,
      logout,
      can,
      updateUser,
    }),
    [state, isLoading, error, login, logout, can, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
